import React, { useMemo, useState } from "react";
import { Dropdown, Form, Button } from "react-bootstrap";
import { FaPlus, FaTimes, FaSearch } from "react-icons/fa";
import "./FilterBar.css";

// Azure Portal-style filter bar. Two kinds of filter defs:
//   - pinned: true  — always visible, e.g. "Status equals all" — click straight into it,
//                     no separate "+ Add filter" step, no remove (x); clearing its value in
//                     the popover (Apply with nothing selected) resets it back to "all".
//   - pinned absent/false — only appears once configured, added via the "+ Add filter"
//                     pill, and removable via an (x) on its chip.
// Filter / Operator / Value are all visible in the popover at once (no progressive reveal),
// matching the reference: https://learn.microsoft.com/azure — the "+ Add filter" popover.
//
// filterDefs: [{
//   key, label, pinned?: boolean,
//   type: 'multiselect' | 'select' | 'dateRange' | 'text' | 'toggle',
//   operators: [{ value, label }],       // not used by 'toggle'
//   options?,                            // multiselect AND select: [{ value, label }]
//   allowCustom?: boolean,               // multiselect only: also offer an "Add "<value>""
//     row for whatever's typed in the search box, so a value not in `options` can still be
//     checked/selected — the checkbox-based counterpart to the old text+datalist combo
//   fromKey?, toKey?,                    // dateRange only
//   inputType?,                          // text only: 'text' | 'number' (default 'text')
//   comboOptions?,                       // text only: string[] — turns the input into a
//     "select with the option to type in too" via a native <datalist>, not a hard-restricted
//     dropdown; pass a fresh array each render if the options are data-derived
//   trueValue?, falseValue?, trueLabel?, falseLabel?,  // toggle only — clicking flips between
//     trueValue/falseValue directly (no popover); defaults: true/false, "Yes"/"No"
// }]
// 'select' vs 'multiselect': 'select' is for a single-value equals field against a fixed,
// small set of options (a bool?, or an enum-like string column matched with ==) — a native
// dropdown, exactly one value or "Any". 'multiselect' is for an IN-style filter where the
// wire format is a comma-separated list and the backend matches ANY of the selected values —
// picking two 'select' options at once wouldn't mean anything to a single-value backend field.
// value: the current filter object (same shape the caller already keeps for the API request).
// onChange(nextValue): called with a full replacement object whenever a filter is applied or
//   removed — the caller doesn't need to know about FilterBar's internal editing state.
function FilterBar({ filterDefs, value, onChange }) {
  // Which chip's popover is open for editing, or null. "add" is the special key for the
  // "+ Add filter" pill's own popover.
  const [openKey, setOpenKey] = useState(null);

  // Draft state for whichever popover is currently open.
  const [draftFilterKey, setDraftFilterKey] = useState("");
  const [draftOperator, setDraftOperator] = useState("");
  const [draftValues, setDraftValues] = useState([]); // multiselect
  const [draftFrom, setDraftFrom] = useState(""); // dateRange
  const [draftTo, setDraftTo] = useState(""); // dateRange
  const [draftText, setDraftText] = useState(""); // text
  const [valueSearch, setValueSearch] = useState("");

  const isActive = (def) => {
    if (def.type === "multiselect") return Array.isArray(value[def.key]) && value[def.key].length > 0;
    if (def.type === "dateRange") return !!(value[def.fromKey] || value[def.toKey]);
    if (def.type === "text" || def.type === "select") return !!value[def.key];
    if (def.type === "toggle") return true; // always has a defined state, never "unset"
    return false;
  };

  // 'toggle' defs are always pinned (there's no popover to "add" them from) and render as a
  // plain click-to-flip chip, handled entirely separately from the popover-based types below.
  const toggleDefs = filterDefs.filter((d) => d.type === "toggle");
  const popoverDefs = filterDefs.filter((d) => d.type !== "toggle");
  const pinnedDefs = popoverDefs.filter((d) => d.pinned);
  const dynamicDefs = popoverDefs.filter((d) => !d.pinned);
  const activeDynamicDefs = dynamicDefs.filter(isActive);
  const inactiveDynamicDefs = dynamicDefs.filter((def) => !isActive(def));
  const draftDef = popoverDefs.find((d) => d.key === draftFilterKey);

  // Infer the operator from the current value rather than storing it separately — "Is after"
  // means only startDate is set, "Is before" only endDate, "Is between" both.
  const inferOperator = (def, source) => {
    if (def.type === "multiselect" || def.type === "text" || def.type === "select") return def.operators[0].value;
    if (def.type === "dateRange") {
      const hasFrom = !!source[def.fromKey];
      const hasTo = !!source[def.toKey];
      if (hasFrom && hasTo) return "between";
      if (hasFrom) return "after";
      if (hasTo) return "before";
      return def.operators[0].value;
    }
    return "";
  };

  const resetDraftForDef = (def) => {
    setDraftFilterKey(def.key);
    setValueSearch("");
    setDraftOperator(inferOperator(def, value));
    if (def.type === "multiselect") {
      setDraftValues(value[def.key] ? [...value[def.key]] : []);
    } else if (def.type === "dateRange") {
      setDraftFrom(value[def.fromKey] || "");
      setDraftTo(value[def.toKey] || "");
    } else if (def.type === "text" || def.type === "select") {
      setDraftText(value[def.key] || "");
    }
  };

  const openAddMenu = () => {
    setOpenKey("add");
    // Default to the first not-yet-applied field so the popover never opens completely blank.
    if (inactiveDynamicDefs.length > 0) resetDraftForDef(inactiveDynamicDefs[0]);
  };

  const openChipEditor = (def) => {
    setOpenKey(def.key);
    resetDraftForDef(def);
  };

  const closePopover = () => {
    setOpenKey(null);
    setDraftFilterKey("");
  };

  // Changing the Filter dropdown mid-edit swaps which field the rest of the popover edits —
  // only reachable from the +Add popover, since a pinned/active chip is already tied to its
  // own field.
  const onChangeFilterField = (key) => {
    const def = popoverDefs.find((d) => d.key === key);
    if (def) resetDraftForDef(def);
  };

  const toggleDraftValue = (optValue) => {
    setDraftValues((prev) => (prev.includes(optValue) ? prev.filter((v) => v !== optValue) : [...prev, optValue]));
  };

  const addCustomValue = () => {
    const custom = valueSearch.trim();
    if (!custom) return;
    setDraftValues((prev) => (prev.includes(custom) ? prev : [...prev, custom]));
    setValueSearch("");
  };

  // Once a field is selected there's always something valid to apply — including "nothing
  // selected"/empty text, which resets a pinned filter back to "all" (Azure doesn't require
  // you to pick a value just to clear one).
  const canApply = !!draftDef;

  const apply = () => {
    if (!draftDef) return;
    const next = { ...value };
    if (draftDef.type === "multiselect") {
      next[draftDef.key] = draftValues;
    } else if (draftDef.type === "dateRange") {
      next[draftDef.fromKey] = draftOperator === "before" ? "" : draftFrom;
      next[draftDef.toKey] = draftOperator === "after" ? "" : draftTo;
    } else if (draftDef.type === "text" || draftDef.type === "select") {
      next[draftDef.key] = draftText;
    }
    onChange(next);
    closePopover();
  };

  const removeFilter = (def) => {
    const next = { ...value };
    if (def.type === "multiselect") next[def.key] = [];
    else if (def.type === "dateRange") {
      next[def.fromKey] = "";
      next[def.toKey] = "";
    } else if (def.type === "text" || def.type === "select") next[def.key] = "";
    onChange(next);
    if (openKey === def.key) closePopover();
  };

  const flipToggle = (def) => {
    const trueValue = def.trueValue ?? true;
    const falseValue = def.falseValue ?? false;
    const isTrue = value[def.key] !== falseValue;
    onChange({ ...value, [def.key]: isTrue ? falseValue : trueValue });
  };

  const operatorWord = (def) => {
    const opValue = inferOperator(def, value);
    return (def.operators.find((o) => o.value === opValue)?.label || "Equals").toLowerCase();
  };

  const valueText = (def) => {
    if (!isActive(def)) return "all";
    if (def.type === "multiselect") {
      const selected = value[def.key] || [];
      const labels = selected.map((v) => def.options.find((o) => o.value === v)?.label || v);
      return labels.length > 2 ? `${labels.slice(0, 2).join(", ")} +${labels.length - 2}` : labels.join(", ");
    }
    if (def.type === "dateRange") {
      const from = value[def.fromKey];
      const to = value[def.toKey];
      if (from && to) return `${from} and ${to}`;
      return from || to;
    }
    if (def.type === "text") return value[def.key];
    if (def.type === "select") return def.options.find((o) => o.value === value[def.key])?.label || value[def.key];
    return "";
  };

  // Custom values already added to the draft (via addCustomValue) but not part of the
  // known `options` list still need a row of their own so they show up checked.
  const optionsForDraft = useMemo(() => {
    if (!draftDef || draftDef.type !== "multiselect") return [];
    if (!draftDef.allowCustom) return draftDef.options;
    const known = new Set(draftDef.options.map((o) => o.value));
    const customOptions = draftValues.filter((v) => !known.has(v)).map((v) => ({ value: v, label: v }));
    return [...draftDef.options, ...customOptions];
  }, [draftDef, draftValues]);

  const filteredOptions = useMemo(() => {
    if (!draftDef || draftDef.type !== "multiselect") return [];
    const q = valueSearch.trim().toLowerCase();
    if (!q) return optionsForDraft;
    return optionsForDraft.filter((o) => o.label.toLowerCase().includes(q));
  }, [draftDef, valueSearch, optionsForDraft]);

  // Only offer "+ Add" when there isn't already an exact match — typing "3" when "3" is a
  // known option should just filter down to it, not offer a redundant custom duplicate.
  const showAddCustomRow =
    draftDef?.type === "multiselect" &&
    draftDef.allowCustom &&
    valueSearch.trim() &&
    !optionsForDraft.some((o) => o.value === valueSearch.trim());

  // Shared body for every popover (a pinned chip, an active dynamic chip, or "+ Add filter")
  // — only the Filter dropdown (fixed once the field is already pinned/applied) differs.
  const renderPopoverBody = (filterFieldIsFixed) => (
    <div className="filter-bar-popover">
      <div className="filter-bar-popover-title">Filter results</div>

      <Form.Group className="mb-3">
        <Form.Label className="small mb-1">Filter</Form.Label>
        {filterFieldIsFixed ? (
          <Form.Control size="sm" value={draftDef?.label || ""} disabled readOnly />
        ) : (
          <Form.Select size="sm" value={draftFilterKey} onChange={(e) => onChangeFilterField(e.target.value)}>
            {inactiveDynamicDefs.length === 0 && <option value="">No more filters available</option>}
            {inactiveDynamicDefs.map((def) => (
              <option key={def.key} value={def.key}>
                {def.label}
              </option>
            ))}
          </Form.Select>
        )}
      </Form.Group>

      {draftDef && (
        <>
          <Form.Group className="mb-3">
            <Form.Label className="small mb-1">Operator</Form.Label>
            <Form.Select size="sm" value={draftOperator} onChange={(e) => setDraftOperator(e.target.value)}>
              {draftDef.operators.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="small mb-1">Value</Form.Label>
            {draftDef.type === "multiselect" && (
              <>
                <div className="filter-bar-search-input">
                  <FaSearch className="filter-bar-search-icon" />
                  <Form.Control
                    size="sm"
                    value={valueSearch}
                    onChange={(e) => setValueSearch(e.target.value)}
                    placeholder="Search values"
                  />
                </div>
                {showAddCustomRow && (
                  <div className="filter-bar-add-custom" role="button" onClick={addCustomValue}>
                    <FaPlus className="me-1" size={10} /> Add &quot;{valueSearch.trim()}&quot;
                  </div>
                )}
                <div className="filter-bar-value-options">
                  {filteredOptions.length === 0 && <div className="text-muted small mt-2">No results</div>}
                  {filteredOptions.map((opt) => (
                    <Form.Check
                      key={opt.value}
                      type="checkbox"
                      id={`filterbar-${draftDef.key}-${opt.value}`}
                      label={opt.label}
                      checked={draftValues.includes(opt.value)}
                      onChange={() => toggleDraftValue(opt.value)}
                      className="mb-1"
                    />
                  ))}
                </div>
              </>
            )}
            {draftDef.type === "dateRange" && (
              <div className="d-flex gap-2">
                {draftOperator !== "before" && (
                  <Form.Control
                    type="date"
                    size="sm"
                    value={draftFrom}
                    onChange={(e) => setDraftFrom(e.target.value)}
                    placeholder="From"
                  />
                )}
                {draftOperator !== "after" && (
                  <Form.Control
                    type="date"
                    size="sm"
                    value={draftTo}
                    onChange={(e) => setDraftTo(e.target.value)}
                    placeholder="To"
                  />
                )}
              </div>
            )}
            {draftDef.type === "select" && (
              <Form.Select size="sm" value={draftText} onChange={(e) => setDraftText(e.target.value)} autoFocus>
                <option value="">Any</option>
                {draftDef.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            )}
            {draftDef.type === "text" && (
              <>
                <Form.Control
                  type={draftDef.inputType || "text"}
                  size="sm"
                  value={draftText}
                  onChange={(e) => setDraftText(e.target.value)}
                  // `list` on an <input> gets a native browser dropdown of suggestions from
                  // the <datalist> below while staying a plain free-typable text field — a
                  // "select box with the option to type in too" needs nothing more than that.
                  list={draftDef.comboOptions?.length ? `filterbar-datalist-${draftDef.key}` : undefined}
                  autoFocus
                />
                {draftDef.comboOptions?.length > 0 && (
                  <datalist id={`filterbar-datalist-${draftDef.key}`}>
                    {draftDef.comboOptions.map((opt) => (
                      <option key={opt} value={opt} />
                    ))}
                  </datalist>
                )}
              </>
            )}
          </Form.Group>
        </>
      )}

      <div className="d-flex gap-2 mt-3">
        <Button variant="light" size="sm" disabled={!canApply} onClick={apply}>
          Apply
        </Button>
        <Button variant="outline-secondary" size="sm" onClick={closePopover}>
          Cancel
        </Button>
      </div>
    </div>
  );

  const renderChip = (def, { removable }) => (
    <Dropdown
      key={def.key}
      show={openKey === def.key}
      onToggle={(open) => (open ? openChipEditor(def) : closePopover())}
    >
      <Dropdown.Toggle as="div" className="filter-bar-chip">
        <span>{def.label}</span>
        <span className="filter-bar-chip-operator mx-1">{operatorWord(def)}</span>
        <span className="fw-bold">{valueText(def)}</span>
        {removable && (
          <FaTimes
            className="filter-bar-chip-remove ms-2"
            onClick={(e) => {
              e.stopPropagation();
              removeFilter(def);
            }}
          />
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu className="p-3">{openKey === def.key && renderPopoverBody(true)}</Dropdown.Menu>
    </Dropdown>
  );

  const renderToggleChip = (def) => {
    const falseValue = def.falseValue ?? false;
    const isTrue = value[def.key] !== falseValue;
    return (
      <div key={def.key} className="filter-bar-chip" role="button" onClick={() => flipToggle(def)}>
        <span>{def.label}</span>
        <span className="fw-bold ms-1">{isTrue ? def.trueLabel ?? "Yes" : def.falseLabel ?? "No"}</span>
      </div>
    );
  };

  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      {toggleDefs.map(renderToggleChip)}
      {pinnedDefs.map((def) => renderChip(def, { removable: false }))}
      {activeDynamicDefs.map((def) => renderChip(def, { removable: true }))}

      {inactiveDynamicDefs.length > 0 && (
        <Dropdown show={openKey === "add"} onToggle={(open) => (open ? openAddMenu() : closePopover())}>
          <Dropdown.Toggle as="div" className="filter-bar-chip filter-bar-add">
            <FaPlus className="me-1" size={11} /> Add filter
          </Dropdown.Toggle>
          <Dropdown.Menu className="p-3">{openKey === "add" && renderPopoverBody(false)}</Dropdown.Menu>
        </Dropdown>
      )}
    </div>
  );
}

export default FilterBar;
